import { StargateClient, QueryClient, setupAuthExtension, setupBankExtension, setupStakingExtension, setupTxExtension, setupDistributionExtension, setupMintExtension, setupGovExtension, GasPrice, calculateFee, SigningStargateClient } from '@cosmjs/stargate';
import BigNumber from 'bignumber.js';
import { cosmos as cosmos$1, onomyprotocol, gravity, getSigningGravityClient, getSigningCosmosClient } from '@onomy/telescope';
import { bech32 } from 'bech32';
import cosmos from 'cosmos-lib';
import { GovParametersType } from '@cosmjs/launchpad/build/lcdapi/gov';
import { Tendermint34Client } from '@cosmjs/tendermint-rpc';
import { ProposalStatus } from 'cosmjs-types/cosmos/gov/v1beta1/gov';

var long = Long;

/**
 * wasm optimizations, to do native i64 multiplication and divide
 */
var wasm = null;

try {
  wasm = new WebAssembly.Instance(new WebAssembly.Module(new Uint8Array([
    0, 97, 115, 109, 1, 0, 0, 0, 1, 13, 2, 96, 0, 1, 127, 96, 4, 127, 127, 127, 127, 1, 127, 3, 7, 6, 0, 1, 1, 1, 1, 1, 6, 6, 1, 127, 1, 65, 0, 11, 7, 50, 6, 3, 109, 117, 108, 0, 1, 5, 100, 105, 118, 95, 115, 0, 2, 5, 100, 105, 118, 95, 117, 0, 3, 5, 114, 101, 109, 95, 115, 0, 4, 5, 114, 101, 109, 95, 117, 0, 5, 8, 103, 101, 116, 95, 104, 105, 103, 104, 0, 0, 10, 191, 1, 6, 4, 0, 35, 0, 11, 36, 1, 1, 126, 32, 0, 173, 32, 1, 173, 66, 32, 134, 132, 32, 2, 173, 32, 3, 173, 66, 32, 134, 132, 126, 34, 4, 66, 32, 135, 167, 36, 0, 32, 4, 167, 11, 36, 1, 1, 126, 32, 0, 173, 32, 1, 173, 66, 32, 134, 132, 32, 2, 173, 32, 3, 173, 66, 32, 134, 132, 127, 34, 4, 66, 32, 135, 167, 36, 0, 32, 4, 167, 11, 36, 1, 1, 126, 32, 0, 173, 32, 1, 173, 66, 32, 134, 132, 32, 2, 173, 32, 3, 173, 66, 32, 134, 132, 128, 34, 4, 66, 32, 135, 167, 36, 0, 32, 4, 167, 11, 36, 1, 1, 126, 32, 0, 173, 32, 1, 173, 66, 32, 134, 132, 32, 2, 173, 32, 3, 173, 66, 32, 134, 132, 129, 34, 4, 66, 32, 135, 167, 36, 0, 32, 4, 167, 11, 36, 1, 1, 126, 32, 0, 173, 32, 1, 173, 66, 32, 134, 132, 32, 2, 173, 32, 3, 173, 66, 32, 134, 132, 130, 34, 4, 66, 32, 135, 167, 36, 0, 32, 4, 167, 11
  ])), {}).exports;
} catch (e) {
  // no wasm support :(
}

/**
 * Constructs a 64 bit two's-complement integer, given its low and high 32 bit values as *signed* integers.
 *  See the from* functions below for more convenient ways of constructing Longs.
 * @exports Long
 * @class A Long class for representing a 64 bit two's-complement integer value.
 * @param {number} low The low (signed) 32 bits of the long
 * @param {number} high The high (signed) 32 bits of the long
 * @param {boolean=} unsigned Whether unsigned or not, defaults to signed
 * @constructor
 */
function Long(low, high, unsigned) {

    /**
     * The low 32 bits as a signed value.
     * @type {number}
     */
    this.low = low | 0;

    /**
     * The high 32 bits as a signed value.
     * @type {number}
     */
    this.high = high | 0;

    /**
     * Whether unsigned or not.
     * @type {boolean}
     */
    this.unsigned = !!unsigned;
}

// The internal representation of a long is the two given signed, 32-bit values.
// We use 32-bit pieces because these are the size of integers on which
// Javascript performs bit-operations.  For operations like addition and
// multiplication, we split each number into 16 bit pieces, which can easily be
// multiplied within Javascript's floating-point representation without overflow
// or change in sign.
//
// In the algorithms below, we frequently reduce the negative case to the
// positive case by negating the input(s) and then post-processing the result.
// Note that we must ALWAYS check specially whether those values are MIN_VALUE
// (-2^63) because -MIN_VALUE == MIN_VALUE (since 2^63 cannot be represented as
// a positive number, it overflows back into a negative).  Not handling this
// case would often result in infinite recursion.
//
// Common constant values ZERO, ONE, NEG_ONE, etc. are defined below the from*
// methods on which they depend.

/**
 * An indicator used to reliably determine if an object is a Long or not.
 * @type {boolean}
 * @const
 * @private
 */
Long.prototype.__isLong__;

Object.defineProperty(Long.prototype, "__isLong__", { value: true });

/**
 * @function
 * @param {*} obj Object
 * @returns {boolean}
 * @inner
 */
function isLong(obj) {
    return (obj && obj["__isLong__"]) === true;
}

/**
 * Tests if the specified object is a Long.
 * @function
 * @param {*} obj Object
 * @returns {boolean}
 */
Long.isLong = isLong;

/**
 * A cache of the Long representations of small integer values.
 * @type {!Object}
 * @inner
 */
var INT_CACHE = {};

/**
 * A cache of the Long representations of small unsigned integer values.
 * @type {!Object}
 * @inner
 */
var UINT_CACHE = {};

/**
 * @param {number} value
 * @param {boolean=} unsigned
 * @returns {!Long}
 * @inner
 */
function fromInt(value, unsigned) {
    var obj, cachedObj, cache;
    if (unsigned) {
        value >>>= 0;
        if (cache = (0 <= value && value < 256)) {
            cachedObj = UINT_CACHE[value];
            if (cachedObj)
                return cachedObj;
        }
        obj = fromBits(value, (value | 0) < 0 ? -1 : 0, true);
        if (cache)
            UINT_CACHE[value] = obj;
        return obj;
    } else {
        value |= 0;
        if (cache = (-128 <= value && value < 128)) {
            cachedObj = INT_CACHE[value];
            if (cachedObj)
                return cachedObj;
        }
        obj = fromBits(value, value < 0 ? -1 : 0, false);
        if (cache)
            INT_CACHE[value] = obj;
        return obj;
    }
}

/**
 * Returns a Long representing the given 32 bit integer value.
 * @function
 * @param {number} value The 32 bit integer in question
 * @param {boolean=} unsigned Whether unsigned or not, defaults to signed
 * @returns {!Long} The corresponding Long value
 */
Long.fromInt = fromInt;

/**
 * @param {number} value
 * @param {boolean=} unsigned
 * @returns {!Long}
 * @inner
 */
function fromNumber(value, unsigned) {
    if (isNaN(value))
        return unsigned ? UZERO : ZERO;
    if (unsigned) {
        if (value < 0)
            return UZERO;
        if (value >= TWO_PWR_64_DBL)
            return MAX_UNSIGNED_VALUE;
    } else {
        if (value <= -TWO_PWR_63_DBL)
            return MIN_VALUE;
        if (value + 1 >= TWO_PWR_63_DBL)
            return MAX_VALUE;
    }
    if (value < 0)
        return fromNumber(-value, unsigned).neg();
    return fromBits((value % TWO_PWR_32_DBL) | 0, (value / TWO_PWR_32_DBL) | 0, unsigned);
}

/**
 * Returns a Long representing the given value, provided that it is a finite number. Otherwise, zero is returned.
 * @function
 * @param {number} value The number in question
 * @param {boolean=} unsigned Whether unsigned or not, defaults to signed
 * @returns {!Long} The corresponding Long value
 */
Long.fromNumber = fromNumber;

/**
 * @param {number} lowBits
 * @param {number} highBits
 * @param {boolean=} unsigned
 * @returns {!Long}
 * @inner
 */
function fromBits(lowBits, highBits, unsigned) {
    return new Long(lowBits, highBits, unsigned);
}

/**
 * Returns a Long representing the 64 bit integer that comes by concatenating the given low and high bits. Each is
 *  assumed to use 32 bits.
 * @function
 * @param {number} lowBits The low 32 bits
 * @param {number} highBits The high 32 bits
 * @param {boolean=} unsigned Whether unsigned or not, defaults to signed
 * @returns {!Long} The corresponding Long value
 */
Long.fromBits = fromBits;

/**
 * @function
 * @param {number} base
 * @param {number} exponent
 * @returns {number}
 * @inner
 */
var pow_dbl = Math.pow; // Used 4 times (4*8 to 15+4)

/**
 * @param {string} str
 * @param {(boolean|number)=} unsigned
 * @param {number=} radix
 * @returns {!Long}
 * @inner
 */
function fromString(str, unsigned, radix) {
    if (str.length === 0)
        throw Error('empty string');
    if (str === "NaN" || str === "Infinity" || str === "+Infinity" || str === "-Infinity")
        return ZERO;
    if (typeof unsigned === 'number') {
        // For goog.math.long compatibility
        radix = unsigned,
        unsigned = false;
    } else {
        unsigned = !! unsigned;
    }
    radix = radix || 10;
    if (radix < 2 || 36 < radix)
        throw RangeError('radix');

    var p;
    if ((p = str.indexOf('-')) > 0)
        throw Error('interior hyphen');
    else if (p === 0) {
        return fromString(str.substring(1), unsigned, radix).neg();
    }

    // Do several (8) digits each time through the loop, so as to
    // minimize the calls to the very expensive emulated div.
    var radixToPower = fromNumber(pow_dbl(radix, 8));

    var result = ZERO;
    for (var i = 0; i < str.length; i += 8) {
        var size = Math.min(8, str.length - i),
            value = parseInt(str.substring(i, i + size), radix);
        if (size < 8) {
            var power = fromNumber(pow_dbl(radix, size));
            result = result.mul(power).add(fromNumber(value));
        } else {
            result = result.mul(radixToPower);
            result = result.add(fromNumber(value));
        }
    }
    result.unsigned = unsigned;
    return result;
}

/**
 * Returns a Long representation of the given string, written using the specified radix.
 * @function
 * @param {string} str The textual representation of the Long
 * @param {(boolean|number)=} unsigned Whether unsigned or not, defaults to signed
 * @param {number=} radix The radix in which the text is written (2-36), defaults to 10
 * @returns {!Long} The corresponding Long value
 */
Long.fromString = fromString;

/**
 * @function
 * @param {!Long|number|string|!{low: number, high: number, unsigned: boolean}} val
 * @param {boolean=} unsigned
 * @returns {!Long}
 * @inner
 */
function fromValue(val, unsigned) {
    if (typeof val === 'number')
        return fromNumber(val, unsigned);
    if (typeof val === 'string')
        return fromString(val, unsigned);
    // Throws for non-objects, converts non-instanceof Long:
    return fromBits(val.low, val.high, typeof unsigned === 'boolean' ? unsigned : val.unsigned);
}

/**
 * Converts the specified value to a Long using the appropriate from* function for its type.
 * @function
 * @param {!Long|number|string|!{low: number, high: number, unsigned: boolean}} val Value
 * @param {boolean=} unsigned Whether unsigned or not, defaults to signed
 * @returns {!Long}
 */
Long.fromValue = fromValue;

// NOTE: the compiler should inline these constant values below and then remove these variables, so there should be
// no runtime penalty for these.

/**
 * @type {number}
 * @const
 * @inner
 */
var TWO_PWR_16_DBL = 1 << 16;

/**
 * @type {number}
 * @const
 * @inner
 */
var TWO_PWR_24_DBL = 1 << 24;

/**
 * @type {number}
 * @const
 * @inner
 */
var TWO_PWR_32_DBL = TWO_PWR_16_DBL * TWO_PWR_16_DBL;

/**
 * @type {number}
 * @const
 * @inner
 */
var TWO_PWR_64_DBL = TWO_PWR_32_DBL * TWO_PWR_32_DBL;

/**
 * @type {number}
 * @const
 * @inner
 */
var TWO_PWR_63_DBL = TWO_PWR_64_DBL / 2;

/**
 * @type {!Long}
 * @const
 * @inner
 */
var TWO_PWR_24 = fromInt(TWO_PWR_24_DBL);

/**
 * @type {!Long}
 * @inner
 */
var ZERO = fromInt(0);

/**
 * Signed zero.
 * @type {!Long}
 */
Long.ZERO = ZERO;

/**
 * @type {!Long}
 * @inner
 */
var UZERO = fromInt(0, true);

/**
 * Unsigned zero.
 * @type {!Long}
 */
Long.UZERO = UZERO;

/**
 * @type {!Long}
 * @inner
 */
var ONE = fromInt(1);

/**
 * Signed one.
 * @type {!Long}
 */
Long.ONE = ONE;

/**
 * @type {!Long}
 * @inner
 */
var UONE = fromInt(1, true);

/**
 * Unsigned one.
 * @type {!Long}
 */
Long.UONE = UONE;

/**
 * @type {!Long}
 * @inner
 */
var NEG_ONE = fromInt(-1);

/**
 * Signed negative one.
 * @type {!Long}
 */
Long.NEG_ONE = NEG_ONE;

/**
 * @type {!Long}
 * @inner
 */
var MAX_VALUE = fromBits(0xFFFFFFFF|0, 0x7FFFFFFF|0, false);

/**
 * Maximum signed value.
 * @type {!Long}
 */
Long.MAX_VALUE = MAX_VALUE;

/**
 * @type {!Long}
 * @inner
 */
var MAX_UNSIGNED_VALUE = fromBits(0xFFFFFFFF|0, 0xFFFFFFFF|0, true);

/**
 * Maximum unsigned value.
 * @type {!Long}
 */
Long.MAX_UNSIGNED_VALUE = MAX_UNSIGNED_VALUE;

/**
 * @type {!Long}
 * @inner
 */
var MIN_VALUE = fromBits(0, 0x80000000|0, false);

/**
 * Minimum signed value.
 * @type {!Long}
 */
Long.MIN_VALUE = MIN_VALUE;

/**
 * @alias Long.prototype
 * @inner
 */
var LongPrototype = Long.prototype;

/**
 * Converts the Long to a 32 bit integer, assuming it is a 32 bit integer.
 * @returns {number}
 */
LongPrototype.toInt = function toInt() {
    return this.unsigned ? this.low >>> 0 : this.low;
};

/**
 * Converts the Long to a the nearest floating-point representation of this value (double, 53 bit mantissa).
 * @returns {number}
 */
LongPrototype.toNumber = function toNumber() {
    if (this.unsigned)
        return ((this.high >>> 0) * TWO_PWR_32_DBL) + (this.low >>> 0);
    return this.high * TWO_PWR_32_DBL + (this.low >>> 0);
};

/**
 * Converts the Long to a string written in the specified radix.
 * @param {number=} radix Radix (2-36), defaults to 10
 * @returns {string}
 * @override
 * @throws {RangeError} If `radix` is out of range
 */
LongPrototype.toString = function toString(radix) {
    radix = radix || 10;
    if (radix < 2 || 36 < radix)
        throw RangeError('radix');
    if (this.isZero())
        return '0';
    if (this.isNegative()) { // Unsigned Longs are never negative
        if (this.eq(MIN_VALUE)) {
            // We need to change the Long value before it can be negated, so we remove
            // the bottom-most digit in this base and then recurse to do the rest.
            var radixLong = fromNumber(radix),
                div = this.div(radixLong),
                rem1 = div.mul(radixLong).sub(this);
            return div.toString(radix) + rem1.toInt().toString(radix);
        } else
            return '-' + this.neg().toString(radix);
    }

    // Do several (6) digits each time through the loop, so as to
    // minimize the calls to the very expensive emulated div.
    var radixToPower = fromNumber(pow_dbl(radix, 6), this.unsigned),
        rem = this;
    var result = '';
    while (true) {
        var remDiv = rem.div(radixToPower),
            intval = rem.sub(remDiv.mul(radixToPower)).toInt() >>> 0,
            digits = intval.toString(radix);
        rem = remDiv;
        if (rem.isZero())
            return digits + result;
        else {
            while (digits.length < 6)
                digits = '0' + digits;
            result = '' + digits + result;
        }
    }
};

/**
 * Gets the high 32 bits as a signed integer.
 * @returns {number} Signed high bits
 */
LongPrototype.getHighBits = function getHighBits() {
    return this.high;
};

/**
 * Gets the high 32 bits as an unsigned integer.
 * @returns {number} Unsigned high bits
 */
LongPrototype.getHighBitsUnsigned = function getHighBitsUnsigned() {
    return this.high >>> 0;
};

/**
 * Gets the low 32 bits as a signed integer.
 * @returns {number} Signed low bits
 */
LongPrototype.getLowBits = function getLowBits() {
    return this.low;
};

/**
 * Gets the low 32 bits as an unsigned integer.
 * @returns {number} Unsigned low bits
 */
LongPrototype.getLowBitsUnsigned = function getLowBitsUnsigned() {
    return this.low >>> 0;
};

/**
 * Gets the number of bits needed to represent the absolute value of this Long.
 * @returns {number}
 */
LongPrototype.getNumBitsAbs = function getNumBitsAbs() {
    if (this.isNegative()) // Unsigned Longs are never negative
        return this.eq(MIN_VALUE) ? 64 : this.neg().getNumBitsAbs();
    var val = this.high != 0 ? this.high : this.low;
    for (var bit = 31; bit > 0; bit--)
        if ((val & (1 << bit)) != 0)
            break;
    return this.high != 0 ? bit + 33 : bit + 1;
};

/**
 * Tests if this Long's value equals zero.
 * @returns {boolean}
 */
LongPrototype.isZero = function isZero() {
    return this.high === 0 && this.low === 0;
};

/**
 * Tests if this Long's value equals zero. This is an alias of {@link Long#isZero}.
 * @returns {boolean}
 */
LongPrototype.eqz = LongPrototype.isZero;

/**
 * Tests if this Long's value is negative.
 * @returns {boolean}
 */
LongPrototype.isNegative = function isNegative() {
    return !this.unsigned && this.high < 0;
};

/**
 * Tests if this Long's value is positive.
 * @returns {boolean}
 */
LongPrototype.isPositive = function isPositive() {
    return this.unsigned || this.high >= 0;
};

/**
 * Tests if this Long's value is odd.
 * @returns {boolean}
 */
LongPrototype.isOdd = function isOdd() {
    return (this.low & 1) === 1;
};

/**
 * Tests if this Long's value is even.
 * @returns {boolean}
 */
LongPrototype.isEven = function isEven() {
    return (this.low & 1) === 0;
};

/**
 * Tests if this Long's value equals the specified's.
 * @param {!Long|number|string} other Other value
 * @returns {boolean}
 */
LongPrototype.equals = function equals(other) {
    if (!isLong(other))
        other = fromValue(other);
    if (this.unsigned !== other.unsigned && (this.high >>> 31) === 1 && (other.high >>> 31) === 1)
        return false;
    return this.high === other.high && this.low === other.low;
};

/**
 * Tests if this Long's value equals the specified's. This is an alias of {@link Long#equals}.
 * @function
 * @param {!Long|number|string} other Other value
 * @returns {boolean}
 */
LongPrototype.eq = LongPrototype.equals;

/**
 * Tests if this Long's value differs from the specified's.
 * @param {!Long|number|string} other Other value
 * @returns {boolean}
 */
LongPrototype.notEquals = function notEquals(other) {
    return !this.eq(/* validates */ other);
};

/**
 * Tests if this Long's value differs from the specified's. This is an alias of {@link Long#notEquals}.
 * @function
 * @param {!Long|number|string} other Other value
 * @returns {boolean}
 */
LongPrototype.neq = LongPrototype.notEquals;

/**
 * Tests if this Long's value differs from the specified's. This is an alias of {@link Long#notEquals}.
 * @function
 * @param {!Long|number|string} other Other value
 * @returns {boolean}
 */
LongPrototype.ne = LongPrototype.notEquals;

/**
 * Tests if this Long's value is less than the specified's.
 * @param {!Long|number|string} other Other value
 * @returns {boolean}
 */
LongPrototype.lessThan = function lessThan(other) {
    return this.comp(/* validates */ other) < 0;
};

/**
 * Tests if this Long's value is less than the specified's. This is an alias of {@link Long#lessThan}.
 * @function
 * @param {!Long|number|string} other Other value
 * @returns {boolean}
 */
LongPrototype.lt = LongPrototype.lessThan;

/**
 * Tests if this Long's value is less than or equal the specified's.
 * @param {!Long|number|string} other Other value
 * @returns {boolean}
 */
LongPrototype.lessThanOrEqual = function lessThanOrEqual(other) {
    return this.comp(/* validates */ other) <= 0;
};

/**
 * Tests if this Long's value is less than or equal the specified's. This is an alias of {@link Long#lessThanOrEqual}.
 * @function
 * @param {!Long|number|string} other Other value
 * @returns {boolean}
 */
LongPrototype.lte = LongPrototype.lessThanOrEqual;

/**
 * Tests if this Long's value is less than or equal the specified's. This is an alias of {@link Long#lessThanOrEqual}.
 * @function
 * @param {!Long|number|string} other Other value
 * @returns {boolean}
 */
LongPrototype.le = LongPrototype.lessThanOrEqual;

/**
 * Tests if this Long's value is greater than the specified's.
 * @param {!Long|number|string} other Other value
 * @returns {boolean}
 */
LongPrototype.greaterThan = function greaterThan(other) {
    return this.comp(/* validates */ other) > 0;
};

/**
 * Tests if this Long's value is greater than the specified's. This is an alias of {@link Long#greaterThan}.
 * @function
 * @param {!Long|number|string} other Other value
 * @returns {boolean}
 */
LongPrototype.gt = LongPrototype.greaterThan;

/**
 * Tests if this Long's value is greater than or equal the specified's.
 * @param {!Long|number|string} other Other value
 * @returns {boolean}
 */
LongPrototype.greaterThanOrEqual = function greaterThanOrEqual(other) {
    return this.comp(/* validates */ other) >= 0;
};

/**
 * Tests if this Long's value is greater than or equal the specified's. This is an alias of {@link Long#greaterThanOrEqual}.
 * @function
 * @param {!Long|number|string} other Other value
 * @returns {boolean}
 */
LongPrototype.gte = LongPrototype.greaterThanOrEqual;

/**
 * Tests if this Long's value is greater than or equal the specified's. This is an alias of {@link Long#greaterThanOrEqual}.
 * @function
 * @param {!Long|number|string} other Other value
 * @returns {boolean}
 */
LongPrototype.ge = LongPrototype.greaterThanOrEqual;

/**
 * Compares this Long's value with the specified's.
 * @param {!Long|number|string} other Other value
 * @returns {number} 0 if they are the same, 1 if the this is greater and -1
 *  if the given one is greater
 */
LongPrototype.compare = function compare(other) {
    if (!isLong(other))
        other = fromValue(other);
    if (this.eq(other))
        return 0;
    var thisNeg = this.isNegative(),
        otherNeg = other.isNegative();
    if (thisNeg && !otherNeg)
        return -1;
    if (!thisNeg && otherNeg)
        return 1;
    // At this point the sign bits are the same
    if (!this.unsigned)
        return this.sub(other).isNegative() ? -1 : 1;
    // Both are positive if at least one is unsigned
    return (other.high >>> 0) > (this.high >>> 0) || (other.high === this.high && (other.low >>> 0) > (this.low >>> 0)) ? -1 : 1;
};

/**
 * Compares this Long's value with the specified's. This is an alias of {@link Long#compare}.
 * @function
 * @param {!Long|number|string} other Other value
 * @returns {number} 0 if they are the same, 1 if the this is greater and -1
 *  if the given one is greater
 */
LongPrototype.comp = LongPrototype.compare;

/**
 * Negates this Long's value.
 * @returns {!Long} Negated Long
 */
LongPrototype.negate = function negate() {
    if (!this.unsigned && this.eq(MIN_VALUE))
        return MIN_VALUE;
    return this.not().add(ONE);
};

/**
 * Negates this Long's value. This is an alias of {@link Long#negate}.
 * @function
 * @returns {!Long} Negated Long
 */
LongPrototype.neg = LongPrototype.negate;

/**
 * Returns the sum of this and the specified Long.
 * @param {!Long|number|string} addend Addend
 * @returns {!Long} Sum
 */
LongPrototype.add = function add(addend) {
    if (!isLong(addend))
        addend = fromValue(addend);

    // Divide each number into 4 chunks of 16 bits, and then sum the chunks.

    var a48 = this.high >>> 16;
    var a32 = this.high & 0xFFFF;
    var a16 = this.low >>> 16;
    var a00 = this.low & 0xFFFF;

    var b48 = addend.high >>> 16;
    var b32 = addend.high & 0xFFFF;
    var b16 = addend.low >>> 16;
    var b00 = addend.low & 0xFFFF;

    var c48 = 0, c32 = 0, c16 = 0, c00 = 0;
    c00 += a00 + b00;
    c16 += c00 >>> 16;
    c00 &= 0xFFFF;
    c16 += a16 + b16;
    c32 += c16 >>> 16;
    c16 &= 0xFFFF;
    c32 += a32 + b32;
    c48 += c32 >>> 16;
    c32 &= 0xFFFF;
    c48 += a48 + b48;
    c48 &= 0xFFFF;
    return fromBits((c16 << 16) | c00, (c48 << 16) | c32, this.unsigned);
};

/**
 * Returns the difference of this and the specified Long.
 * @param {!Long|number|string} subtrahend Subtrahend
 * @returns {!Long} Difference
 */
LongPrototype.subtract = function subtract(subtrahend) {
    if (!isLong(subtrahend))
        subtrahend = fromValue(subtrahend);
    return this.add(subtrahend.neg());
};

/**
 * Returns the difference of this and the specified Long. This is an alias of {@link Long#subtract}.
 * @function
 * @param {!Long|number|string} subtrahend Subtrahend
 * @returns {!Long} Difference
 */
LongPrototype.sub = LongPrototype.subtract;

/**
 * Returns the product of this and the specified Long.
 * @param {!Long|number|string} multiplier Multiplier
 * @returns {!Long} Product
 */
LongPrototype.multiply = function multiply(multiplier) {
    if (this.isZero())
        return ZERO;
    if (!isLong(multiplier))
        multiplier = fromValue(multiplier);

    // use wasm support if present
    if (wasm) {
        var low = wasm.mul(this.low,
                           this.high,
                           multiplier.low,
                           multiplier.high);
        return fromBits(low, wasm.get_high(), this.unsigned);
    }

    if (multiplier.isZero())
        return ZERO;
    if (this.eq(MIN_VALUE))
        return multiplier.isOdd() ? MIN_VALUE : ZERO;
    if (multiplier.eq(MIN_VALUE))
        return this.isOdd() ? MIN_VALUE : ZERO;

    if (this.isNegative()) {
        if (multiplier.isNegative())
            return this.neg().mul(multiplier.neg());
        else
            return this.neg().mul(multiplier).neg();
    } else if (multiplier.isNegative())
        return this.mul(multiplier.neg()).neg();

    // If both longs are small, use float multiplication
    if (this.lt(TWO_PWR_24) && multiplier.lt(TWO_PWR_24))
        return fromNumber(this.toNumber() * multiplier.toNumber(), this.unsigned);

    // Divide each long into 4 chunks of 16 bits, and then add up 4x4 products.
    // We can skip products that would overflow.

    var a48 = this.high >>> 16;
    var a32 = this.high & 0xFFFF;
    var a16 = this.low >>> 16;
    var a00 = this.low & 0xFFFF;

    var b48 = multiplier.high >>> 16;
    var b32 = multiplier.high & 0xFFFF;
    var b16 = multiplier.low >>> 16;
    var b00 = multiplier.low & 0xFFFF;

    var c48 = 0, c32 = 0, c16 = 0, c00 = 0;
    c00 += a00 * b00;
    c16 += c00 >>> 16;
    c00 &= 0xFFFF;
    c16 += a16 * b00;
    c32 += c16 >>> 16;
    c16 &= 0xFFFF;
    c16 += a00 * b16;
    c32 += c16 >>> 16;
    c16 &= 0xFFFF;
    c32 += a32 * b00;
    c48 += c32 >>> 16;
    c32 &= 0xFFFF;
    c32 += a16 * b16;
    c48 += c32 >>> 16;
    c32 &= 0xFFFF;
    c32 += a00 * b32;
    c48 += c32 >>> 16;
    c32 &= 0xFFFF;
    c48 += a48 * b00 + a32 * b16 + a16 * b32 + a00 * b48;
    c48 &= 0xFFFF;
    return fromBits((c16 << 16) | c00, (c48 << 16) | c32, this.unsigned);
};

/**
 * Returns the product of this and the specified Long. This is an alias of {@link Long#multiply}.
 * @function
 * @param {!Long|number|string} multiplier Multiplier
 * @returns {!Long} Product
 */
LongPrototype.mul = LongPrototype.multiply;

/**
 * Returns this Long divided by the specified. The result is signed if this Long is signed or
 *  unsigned if this Long is unsigned.
 * @param {!Long|number|string} divisor Divisor
 * @returns {!Long} Quotient
 */
LongPrototype.divide = function divide(divisor) {
    if (!isLong(divisor))
        divisor = fromValue(divisor);
    if (divisor.isZero())
        throw Error('division by zero');

    // use wasm support if present
    if (wasm) {
        // guard against signed division overflow: the largest
        // negative number / -1 would be 1 larger than the largest
        // positive number, due to two's complement.
        if (!this.unsigned &&
            this.high === -0x80000000 &&
            divisor.low === -1 && divisor.high === -1) {
            // be consistent with non-wasm code path
            return this;
        }
        var low = (this.unsigned ? wasm.div_u : wasm.div_s)(
            this.low,
            this.high,
            divisor.low,
            divisor.high
        );
        return fromBits(low, wasm.get_high(), this.unsigned);
    }

    if (this.isZero())
        return this.unsigned ? UZERO : ZERO;
    var approx, rem, res;
    if (!this.unsigned) {
        // This section is only relevant for signed longs and is derived from the
        // closure library as a whole.
        if (this.eq(MIN_VALUE)) {
            if (divisor.eq(ONE) || divisor.eq(NEG_ONE))
                return MIN_VALUE;  // recall that -MIN_VALUE == MIN_VALUE
            else if (divisor.eq(MIN_VALUE))
                return ONE;
            else {
                // At this point, we have |other| >= 2, so |this/other| < |MIN_VALUE|.
                var halfThis = this.shr(1);
                approx = halfThis.div(divisor).shl(1);
                if (approx.eq(ZERO)) {
                    return divisor.isNegative() ? ONE : NEG_ONE;
                } else {
                    rem = this.sub(divisor.mul(approx));
                    res = approx.add(rem.div(divisor));
                    return res;
                }
            }
        } else if (divisor.eq(MIN_VALUE))
            return this.unsigned ? UZERO : ZERO;
        if (this.isNegative()) {
            if (divisor.isNegative())
                return this.neg().div(divisor.neg());
            return this.neg().div(divisor).neg();
        } else if (divisor.isNegative())
            return this.div(divisor.neg()).neg();
        res = ZERO;
    } else {
        // The algorithm below has not been made for unsigned longs. It's therefore
        // required to take special care of the MSB prior to running it.
        if (!divisor.unsigned)
            divisor = divisor.toUnsigned();
        if (divisor.gt(this))
            return UZERO;
        if (divisor.gt(this.shru(1))) // 15 >>> 1 = 7 ; with divisor = 8 ; true
            return UONE;
        res = UZERO;
    }

    // Repeat the following until the remainder is less than other:  find a
    // floating-point that approximates remainder / other *from below*, add this
    // into the result, and subtract it from the remainder.  It is critical that
    // the approximate value is less than or equal to the real value so that the
    // remainder never becomes negative.
    rem = this;
    while (rem.gte(divisor)) {
        // Approximate the result of division. This may be a little greater or
        // smaller than the actual value.
        approx = Math.max(1, Math.floor(rem.toNumber() / divisor.toNumber()));

        // We will tweak the approximate result by changing it in the 48-th digit or
        // the smallest non-fractional digit, whichever is larger.
        var log2 = Math.ceil(Math.log(approx) / Math.LN2),
            delta = (log2 <= 48) ? 1 : pow_dbl(2, log2 - 48),

        // Decrease the approximation until it is smaller than the remainder.  Note
        // that if it is too large, the product overflows and is negative.
            approxRes = fromNumber(approx),
            approxRem = approxRes.mul(divisor);
        while (approxRem.isNegative() || approxRem.gt(rem)) {
            approx -= delta;
            approxRes = fromNumber(approx, this.unsigned);
            approxRem = approxRes.mul(divisor);
        }

        // We know the answer can't be zero... and actually, zero would cause
        // infinite recursion since we would make no progress.
        if (approxRes.isZero())
            approxRes = ONE;

        res = res.add(approxRes);
        rem = rem.sub(approxRem);
    }
    return res;
};

/**
 * Returns this Long divided by the specified. This is an alias of {@link Long#divide}.
 * @function
 * @param {!Long|number|string} divisor Divisor
 * @returns {!Long} Quotient
 */
LongPrototype.div = LongPrototype.divide;

/**
 * Returns this Long modulo the specified.
 * @param {!Long|number|string} divisor Divisor
 * @returns {!Long} Remainder
 */
LongPrototype.modulo = function modulo(divisor) {
    if (!isLong(divisor))
        divisor = fromValue(divisor);

    // use wasm support if present
    if (wasm) {
        var low = (this.unsigned ? wasm.rem_u : wasm.rem_s)(
            this.low,
            this.high,
            divisor.low,
            divisor.high
        );
        return fromBits(low, wasm.get_high(), this.unsigned);
    }

    return this.sub(this.div(divisor).mul(divisor));
};

/**
 * Returns this Long modulo the specified. This is an alias of {@link Long#modulo}.
 * @function
 * @param {!Long|number|string} divisor Divisor
 * @returns {!Long} Remainder
 */
LongPrototype.mod = LongPrototype.modulo;

/**
 * Returns this Long modulo the specified. This is an alias of {@link Long#modulo}.
 * @function
 * @param {!Long|number|string} divisor Divisor
 * @returns {!Long} Remainder
 */
LongPrototype.rem = LongPrototype.modulo;

/**
 * Returns the bitwise NOT of this Long.
 * @returns {!Long}
 */
LongPrototype.not = function not() {
    return fromBits(~this.low, ~this.high, this.unsigned);
};

/**
 * Returns the bitwise AND of this Long and the specified.
 * @param {!Long|number|string} other Other Long
 * @returns {!Long}
 */
LongPrototype.and = function and(other) {
    if (!isLong(other))
        other = fromValue(other);
    return fromBits(this.low & other.low, this.high & other.high, this.unsigned);
};

/**
 * Returns the bitwise OR of this Long and the specified.
 * @param {!Long|number|string} other Other Long
 * @returns {!Long}
 */
LongPrototype.or = function or(other) {
    if (!isLong(other))
        other = fromValue(other);
    return fromBits(this.low | other.low, this.high | other.high, this.unsigned);
};

/**
 * Returns the bitwise XOR of this Long and the given one.
 * @param {!Long|number|string} other Other Long
 * @returns {!Long}
 */
LongPrototype.xor = function xor(other) {
    if (!isLong(other))
        other = fromValue(other);
    return fromBits(this.low ^ other.low, this.high ^ other.high, this.unsigned);
};

/**
 * Returns this Long with bits shifted to the left by the given amount.
 * @param {number|!Long} numBits Number of bits
 * @returns {!Long} Shifted Long
 */
LongPrototype.shiftLeft = function shiftLeft(numBits) {
    if (isLong(numBits))
        numBits = numBits.toInt();
    if ((numBits &= 63) === 0)
        return this;
    else if (numBits < 32)
        return fromBits(this.low << numBits, (this.high << numBits) | (this.low >>> (32 - numBits)), this.unsigned);
    else
        return fromBits(0, this.low << (numBits - 32), this.unsigned);
};

/**
 * Returns this Long with bits shifted to the left by the given amount. This is an alias of {@link Long#shiftLeft}.
 * @function
 * @param {number|!Long} numBits Number of bits
 * @returns {!Long} Shifted Long
 */
LongPrototype.shl = LongPrototype.shiftLeft;

/**
 * Returns this Long with bits arithmetically shifted to the right by the given amount.
 * @param {number|!Long} numBits Number of bits
 * @returns {!Long} Shifted Long
 */
LongPrototype.shiftRight = function shiftRight(numBits) {
    if (isLong(numBits))
        numBits = numBits.toInt();
    if ((numBits &= 63) === 0)
        return this;
    else if (numBits < 32)
        return fromBits((this.low >>> numBits) | (this.high << (32 - numBits)), this.high >> numBits, this.unsigned);
    else
        return fromBits(this.high >> (numBits - 32), this.high >= 0 ? 0 : -1, this.unsigned);
};

/**
 * Returns this Long with bits arithmetically shifted to the right by the given amount. This is an alias of {@link Long#shiftRight}.
 * @function
 * @param {number|!Long} numBits Number of bits
 * @returns {!Long} Shifted Long
 */
LongPrototype.shr = LongPrototype.shiftRight;

/**
 * Returns this Long with bits logically shifted to the right by the given amount.
 * @param {number|!Long} numBits Number of bits
 * @returns {!Long} Shifted Long
 */
LongPrototype.shiftRightUnsigned = function shiftRightUnsigned(numBits) {
    if (isLong(numBits))
        numBits = numBits.toInt();
    numBits &= 63;
    if (numBits === 0)
        return this;
    else {
        var high = this.high;
        if (numBits < 32) {
            var low = this.low;
            return fromBits((low >>> numBits) | (high << (32 - numBits)), high >>> numBits, this.unsigned);
        } else if (numBits === 32)
            return fromBits(high, 0, this.unsigned);
        else
            return fromBits(high >>> (numBits - 32), 0, this.unsigned);
    }
};

/**
 * Returns this Long with bits logically shifted to the right by the given amount. This is an alias of {@link Long#shiftRightUnsigned}.
 * @function
 * @param {number|!Long} numBits Number of bits
 * @returns {!Long} Shifted Long
 */
LongPrototype.shru = LongPrototype.shiftRightUnsigned;

/**
 * Returns this Long with bits logically shifted to the right by the given amount. This is an alias of {@link Long#shiftRightUnsigned}.
 * @function
 * @param {number|!Long} numBits Number of bits
 * @returns {!Long} Shifted Long
 */
LongPrototype.shr_u = LongPrototype.shiftRightUnsigned;

/**
 * Converts this Long to signed.
 * @returns {!Long} Signed long
 */
LongPrototype.toSigned = function toSigned() {
    if (!this.unsigned)
        return this;
    return fromBits(this.low, this.high, false);
};

/**
 * Converts this Long to unsigned.
 * @returns {!Long} Unsigned long
 */
LongPrototype.toUnsigned = function toUnsigned() {
    if (this.unsigned)
        return this;
    return fromBits(this.low, this.high, true);
};

/**
 * Converts this Long to its byte representation.
 * @param {boolean=} le Whether little or big endian, defaults to big endian
 * @returns {!Array.<number>} Byte representation
 */
LongPrototype.toBytes = function toBytes(le) {
    return le ? this.toBytesLE() : this.toBytesBE();
};

/**
 * Converts this Long to its little endian byte representation.
 * @returns {!Array.<number>} Little endian byte representation
 */
LongPrototype.toBytesLE = function toBytesLE() {
    var hi = this.high,
        lo = this.low;
    return [
        lo        & 0xff,
        lo >>>  8 & 0xff,
        lo >>> 16 & 0xff,
        lo >>> 24       ,
        hi        & 0xff,
        hi >>>  8 & 0xff,
        hi >>> 16 & 0xff,
        hi >>> 24
    ];
};

/**
 * Converts this Long to its big endian byte representation.
 * @returns {!Array.<number>} Big endian byte representation
 */
LongPrototype.toBytesBE = function toBytesBE() {
    var hi = this.high,
        lo = this.low;
    return [
        hi >>> 24       ,
        hi >>> 16 & 0xff,
        hi >>>  8 & 0xff,
        hi        & 0xff,
        lo >>> 24       ,
        lo >>> 16 & 0xff,
        lo >>>  8 & 0xff,
        lo        & 0xff
    ];
};

/**
 * Creates a Long from its byte representation.
 * @param {!Array.<number>} bytes Byte representation
 * @param {boolean=} unsigned Whether unsigned or not, defaults to signed
 * @param {boolean=} le Whether little or big endian, defaults to big endian
 * @returns {Long} The corresponding Long value
 */
Long.fromBytes = function fromBytes(bytes, unsigned, le) {
    return le ? Long.fromBytesLE(bytes, unsigned) : Long.fromBytesBE(bytes, unsigned);
};

/**
 * Creates a Long from its little endian byte representation.
 * @param {!Array.<number>} bytes Little endian byte representation
 * @param {boolean=} unsigned Whether unsigned or not, defaults to signed
 * @returns {Long} The corresponding Long value
 */
Long.fromBytesLE = function fromBytesLE(bytes, unsigned) {
    return new Long(
        bytes[0]       |
        bytes[1] <<  8 |
        bytes[2] << 16 |
        bytes[3] << 24,
        bytes[4]       |
        bytes[5] <<  8 |
        bytes[6] << 16 |
        bytes[7] << 24,
        unsigned
    );
};

/**
 * Creates a Long from its big endian byte representation.
 * @param {!Array.<number>} bytes Big endian byte representation
 * @param {boolean=} unsigned Whether unsigned or not, defaults to signed
 * @returns {Long} The corresponding Long value
 */
Long.fromBytesBE = function fromBytesBE(bytes, unsigned) {
    return new Long(
        bytes[4] << 24 |
        bytes[5] << 16 |
        bytes[6] <<  8 |
        bytes[7],
        bytes[0] << 24 |
        bytes[1] << 16 |
        bytes[2] <<  8 |
        bytes[3],
        unsigned
    );
};

function decodeB32(value) {
  const words = bech32.decode(value);
  return Buffer.from(bech32.fromWords(words.words)).toString(`hex`);
}
function encodeB32(value, prefix = "onomy", type = "hex") {
  const words = bech32.toWords(Buffer.from(value, type));
  return bech32.encode(prefix, words);
}
function validate(value) {
  cosmos.address.getBytes(value);
  return true;
}
const OnomyAddress = {
  encodeB32,
  decodeB32,
  validate
};

class OnomyStargateClient extends StargateClient {
  static async connect(endpoint) {
    const tmClient = await Tendermint34Client.connect(endpoint);
    return new OnomyStargateClient(tmClient);
  }
  constructor(tmClient) {
    super(tmClient);
    this.onomyQueryClient = QueryClient.withExtensions(tmClient, setupAuthExtension, setupBankExtension, setupStakingExtension, setupTxExtension, setupDistributionExtension, setupMintExtension, setupGovExtension);
  }
  getQueryClient() {
    return this.onomyQueryClient;
  }
  async getDenomSupply(denom) {
    const qc = this.getQueryClient();
    const res = await (qc == null ? void 0 : qc.bank.supplyOf(denom));
    if (!res)
      return new BigNumber(0);
    return new BigNumber(res.amount);
  }
  async getValidators() {
    var _a, _b, _c;
    const qc = this.getQueryClient();
    const [bonded, unbonded, unbonding] = await Promise.all([
      (_a = qc == null ? void 0 : qc.staking.validators("BOND_STATUS_BONDED")) != null ? _a : Promise.resolve({ validators: [] }),
      (_b = qc == null ? void 0 : qc.staking.validators("BOND_STATUS_UNBONDED")) != null ? _b : Promise.resolve({ validators: [] }),
      (_c = qc == null ? void 0 : qc.staking.validators("BOND_STATUS_UNBONDING")) != null ? _c : Promise.resolve({ validators: [] })
    ]);
    const res = [...bonded.validators, ...unbonded.validators, ...unbonding.validators];
    return res;
  }
  async getDelegationsForDelegator(delegatorAddress) {
    var _a;
    const qc = this.getQueryClient();
    const res = await (qc == null ? void 0 : qc.staking.delegatorDelegations(delegatorAddress));
    return (_a = res == null ? void 0 : res.delegationResponses) != null ? _a : [];
  }
  async getRewardsForDelegator(delegatorAddress) {
    const qc = this.getQueryClient();
    const res = await (qc == null ? void 0 : qc.distribution.delegationTotalRewards(delegatorAddress));
    return res;
  }
  async getUnbondingDelegations(delegatorAddress) {
    const qc = this.getQueryClient();
    const res = await qc.staking.delegatorUnbondingDelegations(delegatorAddress);
    return res.unbondingResponses;
  }
  async getMintInflation() {
    const qc = this.getQueryClient();
    return qc.mint.inflation();
  }
  async getProposals() {
    const qc = this.getQueryClient();
    const res = await qc.gov.proposals(ProposalStatus.PROPOSAL_STATUS_UNSPECIFIED, "", "");
    return res.proposals;
  }
  async getProposal(id) {
    var _a;
    const qc = this.getQueryClient();
    const res = await qc.gov.proposal(id);
    return (_a = res.proposal) != null ? _a : null;
  }
  async getProposalTally(id) {
    var _a;
    const qc = this.getQueryClient();
    const res = await qc.gov.tally(id);
    return (_a = res.tally) != null ? _a : null;
  }
  async getProposalVotes(id) {
    const qc = this.getQueryClient();
    const res = await qc.gov.votes(id);
    return res.votes;
  }
  async getProposalVoteForAccount(id, voterAddress) {
    var _a, _b;
    const qc = this.getQueryClient();
    try {
      const res = await qc.gov.vote(id, voterAddress);
      return (_a = res.vote) != null ? _a : null;
    } catch (e) {
      if (/not found for proposal/.test((_b = e == null ? void 0 : e.toString()) != null ? _b : ""))
        return null;
      throw e;
    }
  }
  async getMinGovProposalDeposit() {
    var _a, _b, _c;
    const qc = this.getQueryClient();
    const res = await qc.gov.params(GovParametersType.Deposit);
    const [coin] = (_b = (_a = res.depositParams) == null ? void 0 : _a.minDeposit) != null ? _b : [];
    return (_c = coin == null ? void 0 : coin.amount) != null ? _c : "0";
  }
}

const DENOM = "anom";
const DENOM_DECIMAL_PLACES = 18;
const STAKE_REWARD_PEAK_HEIGHT = 100;
const STAKE_REWARD_PEAK_POSITION = 15e7;
const STAKE_REWARD_STD_DEV = 5e7;
const TOTAL_COINS = 3e8;
const GAS_PRICE = GasPrice.fromString(`${0.025}${DENOM}`);
const OnomyConstants = {
  DENOM,
  DENOM_DECIMAL_PLACES,
  STAKE_REWARD_PEAK_HEIGHT,
  STAKE_REWARD_PEAK_POSITION,
  STAKE_REWARD_STD_DEV,
  TOTAL_COINS,
  GAS_PRICE
};

function getInflationRateForSupply(supply) {
  return OnomyConstants.STAKE_REWARD_PEAK_HEIGHT * Math.exp(-1 * (supply - OnomyConstants.STAKE_REWARD_PEAK_POSITION) ** 2 / (2 * OnomyConstants.STAKE_REWARD_STD_DEV ** 2));
}
function getEstYearlyStakingRewardPercentage(totalBridgedAtoms, totalStakedAtoms, inflationRate) {
  const stakingRatio = totalStakedAtoms.div(totalBridgedAtoms).toNumber();
  return inflationRate / stakingRatio * 100;
}
const OnomyFormulas = {
  getInflationRateForSupply,
  getEstYearlyStakingRewardPercentage
};

class OnomyClientError extends Error {
}
class NoOnomySignerError extends OnomyClientError {
  constructor(msg = "No Signer: Install Keplr?") {
    super(msg);
  }
}
const OnomyClientErrors = {
  OnomyClientError,
  NoOnomySignerError
};

function readProposalContent(content) {
  var _a;
  const typeUrl = (_a = content.typeUrl) != null ? _a : "";
  const type = typeUrl.split(".").pop();
  switch (type) {
    case "CommunityPoolSpendProposal":
      return {
        ...cosmos$1.distribution.v1beta1.CommunityPoolSpendProposal.decode(content.value),
        typeUrl: "/cosmos.distribution.v1beta1.CommunityPoolSpendProposal"
      };
    case "CommunityPoolSpendProposalWithDeposit":
      return {
        ...cosmos$1.distribution.v1beta1.CommunityPoolSpendProposalWithDeposit.decode(content.value),
        typeUrl: "/cosmos.distribution.v1beta1.CommunityPoolSpendProposalWithDeposit"
      };
    case "ParameterChangeProposal":
      return {
        ...cosmos$1.params.v1beta1.ParameterChangeProposal.decode(content.value),
        typeUrl: "/cosmos.params.v1beta1.ParameterChangeProposal"
      };
    case "SoftwareUpgradeProposal":
      return {
        ...cosmos$1.upgrade.v1beta1.SoftwareUpgradeProposal.decode(content.value),
        typeUrl: "/cosmos.upgrade.v1beta1.SoftwareUpgradeProposal"
      };
    case "CancelSoftwareUpgradeProposal":
      return {
        ...cosmos$1.upgrade.v1beta1.CancelSoftwareUpgradeProposal.decode(content.value),
        typeUrl: "/cosmos.upgrade.v1beta1.CancelSoftwareUpgradeProposal"
      };
    case "UnhaltBridgeProposal":
      return {
        ...gravity.v1.UnhaltBridgeProposal.decode(content.value),
        typeUrl: "/gravity.v1.UnhaltBridgeProposal"
      };
    case "AirdropProposal":
      return {
        ...gravity.v1.AirdropProposal.decode(content.value),
        typeUrl: "/gravity.v1.AirdropProposal"
      };
    case "IBCMetadataProposal":
      return {
        ...gravity.v1.IBCMetadataProposal.decode(content.value),
        typeUrl: "/gravity.v1.IBCMetadataProposal"
      };
    case "FundTreasuryProposal":
      return {
        ...onomyprotocol.dao.v1.FundTreasuryProposal.decode(content.value),
        typeUrl: "/onomyprotocol.dao.v1.FundTreasuryProposal"
      };
    case "ExchangeWithTreasuryProposal":
      return {
        ...onomyprotocol.dao.v1.ExchangeWithTreasuryProposal.decode(content.value),
        typeUrl: "/onomyprotocol.dao.v1.ExchangeWithTreasuryProposal"
      };
    case "FundAccountProposal":
      return {
        ...onomyprotocol.dao.v1.FundAccountProposal.decode(content.value),
        typeUrl: "/onomyprotocol.dao.v1.FundAccountProposal"
      };
    default:
      return {
        ...cosmos$1.gov.v1beta1.TextProposal.decode(content.value),
        typeUrl: "/unknown"
      };
  }
}
async function readProposal(sg, data, address) {
  var _a, _b, _c, _d;
  const content = data.content ? OnomyGovernance.readProposalContent(data.content) : { description: "unknown", title: "unknown", typeUrl: "/unknown" };
  const id = data.proposalId.toString();
  const [votes, tally, myVote] = await Promise.all([
    sg.getProposalVotes(id),
    sg.getProposalTally(id),
    address ? await sg.getProposalVoteForAccount(id, address) : Promise.resolve(null)
  ]);
  const results = {
    yes: new BigNumber((_a = tally == null ? void 0 : tally.yes) != null ? _a : "0"),
    no: new BigNumber((_b = tally == null ? void 0 : tally.no) != null ? _b : "0"),
    noWithVeto: new BigNumber((_c = tally == null ? void 0 : tally.noWithVeto) != null ? _c : "0"),
    abstain: new BigNumber((_d = tally == null ? void 0 : tally.abstain) != null ? _d : "0")
  };
  return {
    id,
    status: cosmos$1.gov.v1beta1.proposalStatusToJSON(data.status),
    content,
    data,
    votes,
    myVote,
    totalDeposit: data.totalDeposit,
    submitTime: data.submitTime ? new Date(data.submitTime.seconds.toNumber() * 1e3) : null,
    votingEndTime: data.votingEndTime ? new Date(data.votingEndTime.seconds.toNumber() * 1e3) : null,
    votingStartTime: data.votingStartTime ? new Date(data.votingStartTime.seconds.toNumber() * 1e3) : null,
    results: {
      ...results,
      total: results.yes.plus(results.no).plus(results.noWithVeto).plus(results.abstain)
    }
  };
}
const OnomyGovernance = {
  readProposal,
  readProposalContent
};

class OnomyClient {
  constructor(WS_URL, denom = OnomyConstants.DENOM) {
    this.signerFn = null;
    this.denom = OnomyConstants.DENOM;
    this.WS_URL = WS_URL.replace("https://", "wss://");
    this.denom = denom;
    this.ts = onomyprotocol.ClientFactory.createRPCQueryClient({ rpcEndpoint: this.WS_URL });
  }
  bn(val) {
    return new BigNumber(val);
  }
  setSignerFn(signerFn) {
    this.signerFn = signerFn;
  }
  denomDecimalFromFixed(amount, decimalPlaces = OnomyConstants.DENOM_DECIMAL_PLACES) {
    return amount.shiftedBy(-1 * decimalPlaces);
  }
  denomNumberFromFixed(amount, decimalPlaces = OnomyConstants.DENOM_DECIMAL_PLACES) {
    return this.denomDecimalFromFixed(amount, decimalPlaces).toNumber();
  }
  async getAddress() {
    try {
      const signer = this.getSigner();
      const [{ address }] = await signer.getAccounts();
      return address;
    } catch (e) {
      if (e instanceof OnomyClientErrors.NoOnomySignerError) {
        return "";
      }
      throw e;
    }
  }
  async feesForMsgs({
    msgs,
    memo,
    multiplier = 1.3
  }) {
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const sg = await this.getSigningStargate();
    const gasEstimation = await sg.simulate(account.address, msgs, memo);
    const fees = calculateFee(Math.round(gasEstimation * multiplier), OnomyConstants.GAS_PRICE);
    return fees;
  }
  async msgsForDelegate(params) {
    const { validatorAddress, amountFixed, denom = this.denom } = params;
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const delegatorAddress = account.address;
    const delegateMsg = {
      typeUrl: "/cosmos.staking.v1beta1.MsgDelegate",
      value: cosmos$1.staking.v1beta1.MsgDelegate.fromPartial({
        delegatorAddress,
        validatorAddress,
        amount: {
          amount: amountFixed.toFixed(0),
          denom
        }
      })
    };
    return [delegateMsg];
  }
  async feesForDelegate(params) {
    const { feeMultiplier: multiplier, memo } = params;
    const msgs = await this.msgsForDelegate(params);
    return this.feesForMsgs({ msgs, multiplier, memo });
  }
  async delegate(params) {
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const sg = await this.getSigningStargate();
    const [msgs, usedFee] = await Promise.all([
      this.msgsForDelegate(params),
      this.feesForDelegate(params)
    ]);
    return sg.signAndBroadcast(account.address, msgs, usedFee, params.memo);
  }
  async msgsForUndelegate(params) {
    const { validatorAddress, amountFixed, denom = this.denom } = params;
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const delegatorAddress = account.address;
    const undelegateMsg = {
      typeUrl: "/cosmos.staking.v1beta1.MsgUndelegate",
      value: cosmos$1.staking.v1beta1.MsgUndelegate.fromPartial({
        delegatorAddress,
        validatorAddress,
        amount: {
          amount: amountFixed.toFixed(0),
          denom
        }
      })
    };
    return [undelegateMsg];
  }
  async feesForUndelegate(params) {
    const { feeMultiplier: multiplier, memo } = params;
    const msgs = await this.msgsForUndelegate(params);
    return this.feesForMsgs({ msgs, multiplier, memo });
  }
  async undelegate(params) {
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const sg = await this.getSigningStargate();
    const [msgs, usedFee] = await Promise.all([
      this.msgsForUndelegate(params),
      this.feesForUndelegate(params)
    ]);
    return sg.signAndBroadcast(account.address, msgs, usedFee, params.memo);
  }
  async msgsForRedelegate(params) {
    const { srcValidatorAddress, dstValidatorAddress, amountFixed, denom = this.denom } = params;
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const redelegateMsg = {
      typeUrl: "/cosmos.staking.v1beta1.MsgBeginRedelegate",
      value: cosmos$1.staking.v1beta1.MsgBeginRedelegate.fromPartial({
        delegatorAddress: account.address,
        validatorSrcAddress: srcValidatorAddress,
        validatorDstAddress: dstValidatorAddress,
        amount: {
          amount: amountFixed.toFixed(0),
          denom
        }
      })
    };
    return [redelegateMsg];
  }
  async feesForRedelegate(params) {
    const { feeMultiplier: multiplier, memo } = params;
    const msgs = await this.msgsForRedelegate(params);
    return this.feesForMsgs({ msgs, multiplier, memo });
  }
  async redelegate(params) {
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const sg = await this.getSigningStargate();
    const [msgs, usedFee] = await Promise.all([
      this.msgsForRedelegate(params),
      this.feesForRedelegate(params)
    ]);
    return sg.signAndBroadcast(account.address, msgs, usedFee, params.memo);
  }
  async msgsForWithdrawRewards(params) {
    const { validatorAddress } = params;
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const delegatorAddress = account.address;
    const withdrawMsg = {
      typeUrl: "/cosmos.distribution.v1beta1.MsgWithdrawDelegatorReward",
      value: cosmos$1.distribution.v1beta1.MsgWithdrawDelegatorReward.fromPartial({
        delegatorAddress,
        validatorAddress
      })
    };
    return [withdrawMsg];
  }
  async msgsForSendToEth(params) {
    const { denom = this.denom, amountFixed, ethDest } = params;
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const bridgeMsg = {
      typeUrl: "/gravity.v1.MsgSendToEth",
      value: {
        sender: account.address,
        ethDest,
        amount: {
          denom,
          amount: amountFixed.toFixed(0)
        },
        bridgeFee: {
          denom,
          amount: params.bridgeFeeFixed.toFixed(0)
        }
      }
    };
    return [bridgeMsg];
  }
  async feesForWithdrawRewards(params) {
    const { feeMultiplier: multiplier, memo } = params;
    const msgs = await this.msgsForWithdrawRewards(params);
    return this.feesForMsgs({ msgs, multiplier, memo });
  }
  async withdrawRewards(params) {
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const sg = await this.getSigningStargate();
    const [msgs, usedFee] = await Promise.all([
      this.msgsForWithdrawRewards(params),
      this.feesForWithdrawRewards(params)
    ]);
    return sg.signAndBroadcast(account.address, msgs, usedFee, params.memo);
  }
  async msgsForTransferDenom(params) {
    const { destinationAddress: toAddress, amountFixed, denom = this.denom } = params;
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const fromAddress = account.address;
    const sendMsg = {
      typeUrl: "/cosmos.bank.v1beta1.MsgSend",
      value: {
        fromAddress,
        toAddress,
        amount: [{ denom: denom || this.denom, amount: amountFixed.toFixed(0) }]
      }
    };
    return [sendMsg];
  }
  async feesForTransferDenom(params) {
    const { feeMultiplier: multiplier, memo } = params;
    const msgs = await this.msgsForTransferDenom(params);
    return this.feesForMsgs({ msgs, multiplier, memo });
  }
  async transferDenom(params) {
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const sg = await this.getSigningStargate();
    const [msgs, usedFee] = await Promise.all([
      this.msgsForTransferDenom(params),
      this.feesForTransferDenom(params)
    ]);
    return sg.signAndBroadcast(account.address, msgs, usedFee, params.memo);
  }
  async feesForSendToEth(params) {
    const { feeMultiplier: multiplier, memo } = params;
    const msgs = await this.msgsForSendToEth(params);
    return this.feesForMsgs({ msgs, multiplier, memo });
  }
  async sendToEth(params) {
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const sg = await getSigningGravityClient({
      rpcEndpoint: this.WS_URL,
      signer
    });
    console.warn("got signing gravity client");
    const [msgs, usedFee] = await Promise.all([
      this.msgsForSendToEth(params),
      this.feesForSendToEth(params)
    ]);
    const res = await sg.signAndBroadcast(account.address, msgs, usedFee, params.memo);
    return {
      height: res.height,
      transactionHash: res.transactionHash
    };
  }
  async msgsForSubmitProposal(params) {
    var _a;
    const { title, description, recipient, amountFixed } = params;
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const submitPropMsg = {
      typeUrl: "/cosmos.gov.v1beta1.MsgSubmitProposal",
      value: cosmos$1.gov.v1beta1.MsgSubmitProposal.fromPartial({
        proposer: account.address,
        content: {
          typeUrl: "/onomyprotocol.dao.v1.FundAccountProposal",
          value: onomyprotocol.dao.v1.FundAccountProposal.encode(onomyprotocol.dao.v1.FundAccountProposal.fromPartial({
            title,
            description,
            recipient: recipient != null ? recipient : account.address,
            amount: [
              {
                denom: "anom",
                amount: (_a = amountFixed == null ? void 0 : amountFixed.toFixed(0)) != null ? _a : "1"
              }
            ]
          })).finish()
        }
      })
    };
    return [submitPropMsg];
  }
  async feesForSubmitProposal(params) {
    const { feeMultiplier: multiplier, memo } = params;
    const msgs = await this.msgsForSubmitProposal(params);
    return this.feesForMsgs({ msgs, multiplier, memo });
  }
  async submitProposal(params) {
    var _a, _b;
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const sg = await getSigningCosmosClient({ signer, rpcEndpoint: this.WS_URL });
    const [msgs, usedFee] = await Promise.all([
      this.msgsForSubmitProposal(params),
      this.feesForSubmitProposal(params)
    ]);
    const { events = [] } = await sg.signAndBroadcast(account.address, msgs, usedFee, params.memo);
    const spEvt = events.find((evt) => evt.type === "submit_proposal");
    const attrs = (_a = spEvt == null ? void 0 : spEvt.attributes) != null ? _a : [];
    const idAttr = attrs.find((attr) => attr.key === "proposal_id");
    return (_b = idAttr == null ? void 0 : idAttr.value) != null ? _b : null;
  }
  async msgsForVote(params) {
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const voteMsg = {
      typeUrl: "/cosmos.gov.v1beta1.MsgVote",
      value: cosmos$1.gov.v1beta1.MsgVote.fromPartial({
        proposalId: long.fromString(params.proposalId),
        voter: account.address,
        option: params.option
      })
    };
    return [voteMsg];
  }
  async feesForVote(params) {
    const { feeMultiplier: multiplier, memo } = params;
    const msgs = await this.msgsForVote(params);
    return this.feesForMsgs({ msgs, multiplier, memo });
  }
  async vote(params) {
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const sg = await getSigningCosmosClient({ signer, rpcEndpoint: this.WS_URL });
    const [msgs, usedFee] = await Promise.all([this.msgsForVote(params), this.feesForVote(params)]);
    await sg.signAndBroadcast(account.address, msgs, usedFee, params.memo);
  }
  async msgsForDepositToProposal(params) {
    const { amountFixed, proposalId } = params;
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const depositMsg = {
      typeUrl: "/cosmos.gov.v1beta1.MsgDeposit",
      value: cosmos$1.gov.v1beta1.MsgDeposit.fromPartial({
        proposalId: long.fromString(proposalId),
        amount: [
          {
            denom: this.denom,
            amount: amountFixed.toFixed(0)
          }
        ],
        depositor: account.address
      })
    };
    return [depositMsg];
  }
  async feesForDepositToProposal(params) {
    const { feeMultiplier: multiplier, memo } = params;
    const msgs = await this.msgsForDepositToProposal(params);
    return this.feesForMsgs({ msgs, multiplier, memo });
  }
  async depositToProposal(params) {
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const sg = await getSigningCosmosClient({ signer, rpcEndpoint: this.WS_URL });
    const [msgs, usedFee] = await Promise.all([
      this.msgsForDepositToProposal(params),
      this.feesForDepositToProposal(params)
    ]);
    await sg.signAndBroadcast(account.address, msgs, usedFee, params.memo);
  }
  async getProposals() {
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const sg = await this.getStargate();
    const proposalResults = await sg.getProposals();
    return Promise.all(proposalResults.map((p) => OnomyGovernance.readProposal(sg, p, account.address)));
  }
  async getMinSubmitProposalDeposit() {
    const sg = await this.getStargate();
    return sg.getMinGovProposalDeposit();
  }
  async getProposal(id) {
    const signer = this.getSigner();
    const [account] = await signer.getAccounts();
    const sg = await this.getStargate();
    const result = await sg.getProposal(id);
    return result ? OnomyGovernance.readProposal(sg, result, account.address) : result;
  }
  async getAnomSupply() {
    const sg = await this.getStargate();
    const fixed = await sg.getDenomSupply(this.denom);
    return this.denomDecimalFromFixed(fixed);
  }
  async getMintInflation() {
    const sg = await this.getStargate();
    return sg.getMintInflation();
  }
  async getDelegation(validatorAddress, delegatorAddress) {
    var _a;
    const sg = await this.getStargate();
    const res = await sg.getDelegation(delegatorAddress, validatorAddress);
    return new BigNumber((_a = res == null ? void 0 : res.amount) != null ? _a : "0");
  }
  async getDelegationsForValidator(validatorAddress) {
    const sg = await this.getStargate();
    return sg.getDelegationsForDelegator(validatorAddress);
  }
  async getDelegationsForDelegator(delegatorAddress) {
    const sg = await this.getStargate();
    return sg.getDelegationsForDelegator(delegatorAddress);
  }
  async getRewardsForDelegator(delegatorAddress) {
    const sg = await this.getStargate();
    return sg.getRewardsForDelegator(delegatorAddress);
  }
  async getBlockAtHeight(height) {
    const stargate = await this.getStargate();
    const block = await stargate.getBlock(height);
    return block;
  }
  async getValidators() {
    const sg = await this.getStargate();
    return sg.getValidators();
  }
  async getSelfDelegation(validatorAddress) {
    const hexOperatorAddress = OnomyAddress.decodeB32(validatorAddress);
    const delegatorAddress = OnomyAddress.encodeB32(hexOperatorAddress, "onomy");
    return this.getDelegation(validatorAddress, delegatorAddress);
  }
  async getAddressBalance(address, denom) {
    if (!address || !address.length)
      return "0";
    const stargate = await this.getStargate();
    const coin = await stargate.getBalance(address, denom);
    return coin.amount;
  }
  async getUnbondingDelegationsForDelegator(delegatorAddress) {
    const stargate = await this.getStargate();
    return stargate.getUnbondingDelegations(delegatorAddress);
  }
  async getTotalUnbondingForDelegator(delegatorAddress) {
    const unbondings = await this.getUnbondingDelegationsForDelegator(delegatorAddress);
    return unbondings.reduce((sum, validator) => validator.entries.reduce((sumInner, entry) => sumInner.plus(new BigNumber(entry.balance)), sum), new BigNumber(0));
  }
  async getValidatorsForDelegator(delegatorAddress) {
    if (!delegatorAddress || !delegatorAddress.length)
      return [];
    const [validators, delegationData, inflationRate, unbondings, rewardsData] = await Promise.all([
      this.getValidators(),
      delegatorAddress ? this.getDelegationsForDelegator(delegatorAddress) : Promise.resolve([]),
      this.getMintInflation(),
      this.getUnbondingDelegationsForDelegator(delegatorAddress),
      delegatorAddress ? this.getRewardsForDelegator(delegatorAddress) : Promise.resolve(null)
    ]);
    const totalStakedAtoms = validators.reduce((sum, v) => new BigNumber(v.tokens).plus(sum), new BigNumber(0));
    const APR = await this.getEstYearlyStakingReward();
    return Promise.all(validators.map(async (validator) => {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q;
      const rewardItems = rewardsData == null ? void 0 : rewardsData.rewards.find((v) => v.validatorAddress === validator.operatorAddress);
      const rewards = (_a = rewardItems == null ? void 0 : rewardItems.reward.find((r) => r.denom === OnomyConstants.DENOM)) != null ? _a : null;
      const selfDelegation = await this.getSelfDelegation(validator.operatorAddress);
      const selfStakeRate = selfDelegation.div(validator.tokens);
      const unbonding = (_b = unbondings.find((x) => x.validatorAddress === validator.operatorAddress)) != null ? _b : null;
      const delegation = delegationData.find((d) => {
        var _a2;
        return ((_a2 = d.delegation) == null ? void 0 : _a2.validatorAddress) === validator.operatorAddress;
      });
      const staked = new BigNumber(validator.tokens);
      return {
        id: validator.operatorAddress,
        validator: {
          name: (_d = (_c = validator.description) == null ? void 0 : _c.moniker) != null ? _d : validator.operatorAddress,
          website: (_f = (_e = validator.description) == null ? void 0 : _e.website) != null ? _f : "",
          securityContact: (_h = (_g = validator.description) == null ? void 0 : _g.securityContact) != null ? _h : "",
          details: (_j = (_i = validator.description) == null ? void 0 : _i.details) != null ? _j : "",
          votingPowerRatio: staked.div(totalStakedAtoms).toNumber(),
          isActive: validator.status === cosmos$1.staking.v1beta1.BondStatus.BOND_STATUS_BONDED
        },
        totalStaked: new BigNumber(validator.tokens),
        selfStaked: selfDelegation,
        selfStakeRatio: selfStakeRate ? selfStakeRate.toNumber() : 0,
        rewards: {
          APR,
          commissionRate: this.denomNumberFromFixed(new BigNumber((_m = (_l = (_k = validator.commission) == null ? void 0 : _k.commissionRates) == null ? void 0 : _l.rate) != null ? _m : "0")) * 100,
          amount: new BigNumber((_n = rewards == null ? void 0 : rewards.amount) != null ? _n : "0").shiftedBy(-18)
        },
        unbonding: (_o = unbonding == null ? void 0 : unbonding.entries) != null ? _o : [],
        delegated: new BigNumber((_q = (_p = delegation == null ? void 0 : delegation.balance) == null ? void 0 : _p.amount) != null ? _q : "0")
      };
    }));
  }
  async getValidatorForDelegator(delegator, validatorAddress) {
    var _a;
    const validators = await this.getValidatorsForDelegator(delegator);
    return (_a = validators.find((v) => v.id === validatorAddress)) != null ? _a : null;
  }
  async getTreasury() {
    var _a;
    const ts = await this.ts;
    const res = await ts.onomyprotocol.dao.v1.treasury();
    const anom = res.treasuryBalance.find((x) => x.denom === this.denom);
    return this.denomDecimalFromFixed(new BigNumber((_a = anom == null ? void 0 : anom.amount) != null ? _a : "0"));
  }
  async getActiveSupply() {
    const [total, treasury] = await Promise.all([this.getAnomSupply(), this.getTreasury()]);
    console.log({
      total: total.toString(),
      treasury: treasury.toString()
    });
    return total.minus(treasury);
  }
  async getEstYearlyStakingReward() {
    const [validators, bridgedSupplyNom, inflationRate] = await Promise.all([
      this.getValidators(),
      this.getActiveSupply(),
      this.getMintInflation()
    ]);
    const totalStakedAtoms = validators.reduce((sum, v) => new BigNumber(v.tokens).plus(sum), new BigNumber(0));
    return OnomyFormulas.getEstYearlyStakingRewardPercentage(bridgedSupplyNom.shiftedBy(18), totalStakedAtoms, inflationRate.toFloatApproximation());
  }
  getStargate() {
    if (this.stargate)
      return this.stargate;
    this.stargate = this.connectStargate();
    return this.stargate;
  }
  getSigningStargate() {
    if (this.signingStargate)
      return this.signingStargate;
    this.signingStargate = this.connectSigningStargate();
    return this.signingStargate;
  }
  connectStargate() {
    try {
      this.stargate = OnomyStargateClient.connect(this.WS_URL);
      return this.stargate;
    } catch (e) {
      console.error("Error connecting stargate to", this.WS_URL, e);
      throw e;
    }
  }
  connectSigningStargate() {
    try {
      const signer = this.getSigner();
      this.signingStargate = SigningStargateClient.connectWithSigner(this.WS_URL, signer);
      if (!this.signingStargate)
        throw new Error("Null signingStargate");
      return this.signingStargate;
    } catch (e) {
      console.error("Error connecting signing stargate to", this.WS_URL, e);
      throw e;
    }
  }
  getSigner() {
    if (!this.signerFn)
      throw new OnomyClientErrors.NoOnomySignerError();
    const signer = this.signerFn();
    if (!signer)
      throw new OnomyClientErrors.NoOnomySignerError();
    return signer;
  }
}

export { OnomyAddress, OnomyClient, OnomyClientErrors, OnomyConstants, OnomyFormulas };
